import { UcoastEl } from '@/scripts/core/UcoastEl'
import EmblaCarousel, { EmblaOptionsType } from 'embla-carousel'
import { EmblaCarouselType } from 'embla-carousel'
import { qsRequired, qsaOptional } from '@/scripts/core/global'
import { WheelGesturesPlugin } from 'embla-carousel-wheel-gestures'
import ClassNames from 'embla-carousel-class-names'

export class ProductGallery extends UcoastEl {
	static htmlSelector = 'product-gallery'
	currentCount: HTMLElement
	totalCount: HTMLElement
	allSlidesLoaded = false
	constructor() {
		super()
		const OPTIONS: EmblaOptionsType = {
			inViewThreshold: 1,
			containScroll: 'keepSnaps',
			align: 'start',
		}

		const THUMB_OPTIONS: EmblaOptionsType = {
			containScroll: 'keepSnaps',
			dragFree: true,
			axis: 'y',
		}

		this.currentCount = qsRequired(
			'[data-embla-current-slide-number]',
			this
		)
		this.totalCount = qsRequired('[data-embla-current-slide-total]', this)

		const viewportNode = qsRequired('.product-gallery__main .embla__viewport', this)
		const prevBtn = qsRequired('.embla__button--prev', this)
		const nextBtn = qsRequired('.embla__button--next', this)
		const emblaApi = EmblaCarousel(viewportNode, OPTIONS, [
			WheelGesturesPlugin(),
			ClassNames(),
		])

		emblaApi.on('select', (event) => {
			const index = event.selectedScrollSnap()
			event.slideNodes().forEach((slideNode, i) => {
				if (i == index) {
					window.Ucoast.mediaManager.loadAllInContainer(slideNode)
				}
			})

			if (this.allSlidesLoaded) return
			void window.Ucoast.mediaManager.preloadContainer(this)
			this.allSlidesLoaded = true
		})

		const removePrevNextBtnsClickHandlers = addPrevNextBtnsClickHandlers(
			emblaApi,
			prevBtn,
			nextBtn
		)

		const removeSlideCounter = addSlideCounter(
			emblaApi,
			this.currentCount,
			this.totalCount
		)

		// Thumbnails
		const thumbsNode = this.querySelector<HTMLElement>('[data-embla-thumbs] .embla__viewport')
		if (thumbsNode) {
			// On desktop use vertical axis; on mobile use horizontal
			const isDesktop = window.matchMedia('(min-width: 750px)').matches
			const thumbAxis = isDesktop ? 'y' : 'x'

			const thumbsApi = EmblaCarousel(thumbsNode, {
				...THUMB_OPTIONS,
				axis: thumbAxis,
			}, [WheelGesturesPlugin()])

			const thumbSlides = qsaOptional<HTMLElement>('.embla--thumbs__slide', this)

			// Click on thumb → scroll main carousel
			thumbSlides.forEach((slide, index) => {
				const btn = slide.querySelector('button')
				btn?.addEventListener('click', () => {
					emblaApi.scrollTo(index)
				})
			})

			// Main carousel select → update active thumb + scroll thumbs into view
			const syncThumb = () => {
				const index = emblaApi.selectedScrollSnap()
				thumbSlides.forEach((slide, i) => {
					slide.classList.toggle('is-active', i === index)
				})
				thumbsApi.scrollTo(index)
			}

			emblaApi.on('select', syncThumb)
			emblaApi.on('init', syncThumb)
			emblaApi.on('reInit', syncThumb)

			// Re-init thumbs when viewport resizes (axis may change)
			window.matchMedia('(min-width: 750px)').addEventListener('change', (e) => {
				thumbsApi.reInit({ axis: e.matches ? 'y' : 'x' })
			})

			emblaApi.on('destroy', () => thumbsApi.destroy())
		}

		emblaApi.on('destroy', removePrevNextBtnsClickHandlers)
		emblaApi.on('destroy', removeSlideCounter)
	}
}

function addSlideCounter(
	emblaApi: EmblaCarouselType,
	currentCount: HTMLElement,
	totalCount: HTMLElement
): () => void {
	const togglePrevNextBtnsState = (): void => {
		const currentSlide = emblaApi.selectedScrollSnap() + 1
		const totalSlides = emblaApi.slideNodes().length
		currentCount.textContent = currentSlide.toString()
		totalCount.textContent = totalSlides.toString()
	}

	emblaApi.on('select', togglePrevNextBtnsState)
	return (): void => {}
}

const addTogglePrevNextBtnsActive = (
	emblaApi: EmblaCarouselType,
	prevBtn: HTMLElement,
	nextBtn: HTMLElement
): (() => void) => {
	const togglePrevNextBtnsState = (): void => {
		if (emblaApi.canScrollPrev()) prevBtn.removeAttribute('disabled')
		else prevBtn.setAttribute('disabled', 'disabled')

		if (emblaApi.canScrollNext()) nextBtn.removeAttribute('disabled')
		else nextBtn.setAttribute('disabled', 'disabled')
	}

	emblaApi
		.on('select', togglePrevNextBtnsState)
		.on('init', togglePrevNextBtnsState)
		.on('reInit', togglePrevNextBtnsState)

	return (): void => {
		prevBtn.removeAttribute('disabled')
		nextBtn.removeAttribute('disabled')
	}
}

export const addPrevNextBtnsClickHandlers = (
	emblaApi: EmblaCarouselType,
	prevBtn: HTMLElement,
	nextBtn: HTMLElement
): (() => void) => {
	const scrollPrev = () => emblaApi.scrollPrev()
	const scrollNext = () => emblaApi.scrollNext()
	prevBtn.addEventListener('click', scrollPrev, false)
	nextBtn.addEventListener('click', scrollNext, false)

	const removeTogglePrevNextBtnsActive = addTogglePrevNextBtnsActive(
		emblaApi,
		prevBtn,
		nextBtn
	)

	return (): void => {
		removeTogglePrevNextBtnsActive()
		prevBtn.removeEventListener('click', scrollPrev, false)
		nextBtn.removeEventListener('click', scrollNext, false)
	}
}
